import {
  Fragment,
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from "react";
import Icon from "@mdi/react";
import {
  mdiCheck,
  mdiChevronDown,
  mdiChevronRight,
  mdiClose,
  mdiCloudUploadOutline,
  mdiDownload,
  mdiMenuDown,
  mdiMenuUp,
  mdiRefresh,
} from "@mdi/js";
import { debounce } from "lodash";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { urls } from "../../../utils/urls";
import { ACTION, MODULE } from "../../../utils/enums";
import { useAuth } from "../../../context_providers/auth/authProvider";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../context_providers/modal/loader";
import Pagination from "../../../ui/new_components/common/pagination";
import WarehouseSearchField from "../warehouse/warehouse_search_field";
import { WarehouseModel } from "../models/warehouse_model";
import {
  ApproveStockActionRequestApi,
  CancelStockActionRequestApi,
  InventoryAuditAction,
  ListStockActionRequestsApi,
  RejectStockActionRequestApi,
  StockActionRequest,
  StockActionRequestFilter,
  StockRequestStatus,
} from "./bulk_stock_upload/bulk_stock_upload_repo";
import StockActionPreviewPanel from "./bulk_stock_upload/StockActionPreviewPanel";

const defaultFilter = (): StockActionRequestFilter => ({ page: 1, count: 20 });

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    [StockRequestStatus.pending]: "bg-amber-100 text-amber-700",
    [StockRequestStatus.approved]: "bg-green-100 text-green-700",
    [StockRequestStatus.rejected]: "bg-red-100 text-red-700",
    [StockRequestStatus.cancelled]: "bg-gray-200 text-gray-600",
  };
  return map[status] ?? "bg-gray-100 text-gray-600";
};

const StockActionRequestsScreen: FunctionComponent = () => {
  const { showToast } = useToast();
  const { isAuthorised } = useAuth();
  const history = useHistory();

  const canApprove = isAuthorised({
    module: MODULE.audit_stock_upload,
    action: ACTION.UPDATE,
  });
  const canRaise = isAuthorised({
    module: MODULE.audit_stock_upload,
    action: ACTION.CREATE,
  });

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<StockActionRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [warehouse, setWarehouse] = useState<WarehouseModel | null>(null);
  const [filter, setFilter] =
    useState<StockActionRequestFilter>(defaultFilter());
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const toggleExpanded = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const fetchData = async (prop: StockActionRequestFilter) => {
    setLoading(true);
    try {
      const res = await ListStockActionRequestsApi(prop);
      if (res?.success) {
        const requests = res.data?.requests ?? [];
        setData(Array.isArray(requests) ? requests : []);
        const meta = res.data?.metadata;
        setTotal(Array.isArray(meta) ? (meta[0]?.total ?? 0) : 0);
      } else {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to fetch requests",
        });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandler = useCallback(
    debounce((prop: StockActionRequestFilter) => {
      if (prop) fetchData(prop);
    }, 300),
    [],
  );

  useEffect(() => {
    debouncedHandler(filter);
  }, [filter, debouncedHandler]);

  const clearFilter = () => {
    setWarehouse(null);
    setFilter(defaultFilter());
  };

  const onApprove = async (id: string) => {
    setLoading(true);
    try {
      const res = await ApproveStockActionRequestApi(id);
      if (res?.success) {
        showToast({ type: ToastType.success, text: res.message || "Approved" });
        fetchData(filter);
      } else {
        showToast({
          type: ToastType.error,
          text: res?.message || res?.error || "Failed to approve",
        });
      }
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e.message });
    } finally {
      setLoading(false);
    }
  };

  const onReject = async () => {
    if (!rejectingId || !rejectReason.trim()) return;
    setLoading(true);
    try {
      const res = await RejectStockActionRequestApi(
        rejectingId,
        rejectReason.trim(),
      );
      if (res?.success) {
        showToast({ type: ToastType.success, text: "Request rejected" });
        setRejectingId(null);
        setRejectReason("");
        fetchData(filter);
      } else {
        showToast({
          type: ToastType.error,
          text: res?.message || res?.error || "Failed to reject",
        });
      }
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e.message });
    } finally {
      setLoading(false);
    }
  };

  const onCancel = async (id: string) => {
    setLoading(true);
    try {
      const res = await CancelStockActionRequestApi(id);
      if (res?.success) {
        showToast({ type: ToastType.success, text: "Request cancelled" });
        fetchData(filter);
      } else {
        showToast({
          type: ToastType.error,
          text: res?.message || res?.error || "Failed to cancel",
        });
      }
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        id="page_container"
        className="flex flex-col h-full p-1 overflow-hidden"
      >
        <section className="flex justify-between items-center my-1 flex-wrap gap-2 px-1">
          <h1 className="font-bold text-lg md:text-2xl text-gray-500 flex items-center gap-2">
            Stock Requests
            <button
              id="refresh_btn"
              title="Refresh"
              onClick={() => fetchData(filter)}
            >
              <Icon path={mdiRefresh} className="h-5 w-5 md:h-6 md:w-6" />
            </button>
          </h1>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              id="toggle_filters_btn"
              onClick={() => setShowFilters((s) => !s)}
              className="md:hidden flex gap-1 items-center rounded px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300"
            >
              <Icon
                path={showFilters ? mdiMenuUp : mdiMenuDown}
                className="w-4 h-4"
              />
              Filters
            </button>
            <button
              id="clear_filter_btn"
              onClick={clearFilter}
              className="flex gap-1 items-center cursor-pointer rounded px-2 py-1 bg-myPrimaryColor hover:scale-105 duration-200 text-xs md:text-sm text-white transform"
            >
              Clear Filter
            </button>
            {canRaise && (
              <button
                id="raise_request_btn"
                onClick={() => history.push(urls.bulk_stock_upload)}
                className="flex gap-1 items-center cursor-pointer rounded px-2 py-1 bg-myPrimaryColor hover:scale-105 duration-200 text-xs md:text-sm text-white transform"
              >
                <Icon path={mdiCloudUploadOutline} size="15" />
                Raise New Request
              </button>
            )}
          </div>
        </section>

        <section
          className={`${
            showFilters ? "grid" : "hidden"
          } md:grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 px-1 my-2`}
        >
          <div className="flex flex-col gap-1">
            <label className="text-xs md:text-sm font-semibold">Status</label>
            <select
              id="status_filter"
              className="w-full h-8 px-2 bg-white rounded border text-xs md:text-sm focus:outline-none"
              value={filter.status ?? ""}
              onChange={(e) =>
                setFilter((p) => ({
                  ...p,
                  page: 1,
                  status: e.target.value || undefined,
                }))
              }
            >
              <option value="">All</option>
              {Object.values(StockRequestStatus).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs md:text-sm font-semibold">Action</label>
            <select
              id="action_filter"
              className="w-full h-8 px-2 bg-white rounded border text-xs md:text-sm focus:outline-none"
              value={filter.action ?? ""}
              onChange={(e) =>
                setFilter((p) => ({
                  ...p,
                  page: 1,
                  action: e.target.value || undefined,
                }))
              }
            >
              <option value="">All</option>
              {Object.values(InventoryAuditAction).map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs md:text-sm font-semibold">
              Warehouse
            </label>
            <WarehouseSearchField
              id="warehouse_filter"
              value={warehouse || undefined}
              placeholder="Select warehouse"
              onSelect={(w) => {
                setWarehouse(w);
                setFilter((p) => ({ ...p, page: 1, warehouse_id: w.id }));
              }}
              onClear={() => {
                setWarehouse(null);
                setFilter((p) => ({
                  ...p,
                  page: 1,
                  warehouse_id: undefined,
                }));
              }}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs md:text-sm font-semibold">
              Item Code
            </label>
            <input
              id="item_code_filter"
              type="text"
              placeholder="Item code"
              className="w-full h-8 px-2 rounded border text-xs md:text-sm focus:outline-none"
              value={filter.item_code ?? ""}
              onChange={(e) =>
                setFilter((p) => ({
                  ...p,
                  page: 1,
                  item_code: e.target.value || undefined,
                }))
              }
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs md:text-sm font-semibold">
              From Date
            </label>
            <input
              id="from_date_filter"
              type="date"
              className="w-full h-8 px-2 rounded border text-xs md:text-sm focus:outline-none"
              value={filter.from_date ?? ""}
              onChange={(e) =>
                setFilter((p) => ({
                  ...p,
                  page: 1,
                  from_date: e.target.value || undefined,
                }))
              }
            />
          </div>
        </section>

        <div className="my-2 border rounded overflow-auto flex-grow">
          <table className="w-full text-xs divide-y divide-gray-200 relative whitespace-nowrap">
            <thead className="text-xs py-2 sticky -top-1 bg-white z-10">
              <tr className="border">
                <th className="px-2 py-3 w-8"></th>
                <th className="px-4 py-3 whitespace-nowrap text-left">
                  Action
                </th>
                <th className="px-4 py-3 whitespace-nowrap text-left">
                  Warehouse
                </th>
                <th className="px-4 py-3 whitespace-nowrap text-left">
                  Item Code
                </th>
                <th className="px-4 py-3 whitespace-nowrap text-left">
                  To Item Code
                </th>
                <th className="px-4 py-3 whitespace-nowrap text-right">
                  Will Apply
                </th>
                <th className="px-4 py-3 whitespace-nowrap text-left">
                  Status
                </th>
                <th className="px-4 py-3 whitespace-nowrap text-left">
                  Raised By
                </th>
                <th className="px-4 py-3 whitespace-nowrap text-left">
                  Raised At
                </th>
                <th className="px-4 py-3 whitespace-nowrap text-left">File</th>
                <th className="px-4 py-3 whitespace-nowrap text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {data?.length > 0 ? (
                data.map((row, i) => {
                  const key = row.id ?? `${i}`;
                  const isExpanded = expandedRows.has(key);
                  const isPending = row.status === StockRequestStatus.pending;
                  const isRejected = row.status === StockRequestStatus.rejected;
                  return (
                    <Fragment key={key}>
                      <tr id={`request_row_${key}`} className="border">
                        <td className="px-2 py-3 text-center">
                          <button
                            id={`request_expand_btn_${key}`}
                            title={isExpanded ? "Collapse" : "Expand"}
                            onClick={() => toggleExpanded(key)}
                          >
                            <Icon
                              path={
                                isExpanded ? mdiChevronDown : mdiChevronRight
                              }
                              size={0.7}
                            />
                          </button>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row.action ?? "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row.warehouse_name ?? "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row.item_code ?? "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row.to_item_code ?? "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          {row.preview?.counts?.will_apply ?? "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          <span
                            className={`px-2 py-0.5 rounded-full ${statusBadge(
                              row.status,
                            )}`}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row.created_by_name ?? "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row.created_at
                            ? moment(row.created_at).format("DD-MM-YYYY h:mm a")
                            : "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row.file_url ? (
                            <a
                              id={`request_file_link_${key}`}
                              href={row.file_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 flex items-center gap-1 hover:text-blue-700 underline"
                            >
                              <Icon path={mdiDownload} size={0.6} />
                              {row.file_name ?? "Download"}
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          <div className="flex gap-2">
                            {canApprove && isPending && (
                              <>
                                <button
                                  id={`approve_btn_${key}`}
                                  title="Approve"
                                  onClick={() => onApprove(key)}
                                  className="flex items-center gap-1 text-green-700 border border-green-600 rounded px-2 py-0.5 hover:bg-green-50"
                                >
                                  <Icon path={mdiCheck} size={0.6} /> Approve
                                </button>
                                <button
                                  id={`reject_btn_${key}`}
                                  title="Reject"
                                  onClick={() => {
                                    setRejectingId(key);
                                    setRejectReason("");
                                  }}
                                  className="flex items-center gap-1 text-red-600 border border-red-500 rounded px-2 py-0.5 hover:bg-red-50"
                                >
                                  <Icon path={mdiClose} size={0.6} /> Reject
                                </button>
                              </>
                            )}
                            {(isPending || isRejected) && (
                              <button
                                id={`edit_request_btn_${key}`}
                                title="Edit & resubmit"
                                onClick={() =>
                                  history.push(
                                    `${urls.stock_action_requests}/${key}/edit`,
                                  )
                                }
                                className="text-blue-600 border border-blue-500 rounded px-2 py-0.5 hover:bg-blue-50"
                              >
                                Edit
                              </button>
                            )}
                            {isPending && (
                              <button
                                id={`cancel_request_btn_${key}`}
                                title="Cancel"
                                onClick={() => onCancel(key)}
                                className="text-gray-600 border rounded px-2 py-0.5 hover:bg-gray-100"
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50" key={`${key}-details`}>
                          <td></td>
                          <td colSpan={10} className="px-4 py-3 space-y-3">
                            {row.reject_reason && (
                              <div className="text-xs border border-red-200 bg-red-50 text-red-700 rounded-md p-2">
                                <span className="font-semibold">
                                  Reject reason:
                                </span>{" "}
                                {row.reject_reason}
                              </div>
                            )}
                            {row.remarks && (
                              <div className="text-xs text-gray-600">
                                <span className="font-semibold">Remarks:</span>{" "}
                                {row.remarks}
                              </div>
                            )}
                            {row.preview ? (
                              <StockActionPreviewPanel preview={row.preview} />
                            ) : (
                              <p className="text-xs text-gray-400">
                                No preview stored.
                              </p>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={11}>
                    <div className="h-56 text-center p-5 my-20">
                      <h5 className="text-gray-400 text-2xl">No Data found!</h5>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div id="pagination_container" className="mt-3">
          <Pagination
            itemCount={data.length}
            page={filter.page}
            total={total}
            count={filter.count}
            onChange={(page) => setFilter((o) => ({ ...o, page }))}
          />
        </div>
      </div>

      {rejectingId && (
        <div
          id="reject_request_modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        >
          <div className="bg-white rounded-lg p-5 w-full max-w-md space-y-3">
            <h3 className="font-semibold text-lg">Reject Request</h3>
            <textarea
              id="reject_reason_input"
              autoFocus
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection (required)"
              rows={3}
              className="w-full border rounded-md px-3 py-2 text-sm"
            />
            <div className="flex justify-end gap-3">
              <button
                id="reject_modal_cancel_btn"
                onClick={() => {
                  setRejectingId(null);
                  setRejectReason("");
                }}
                className="border px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
              <button
                id="reject_modal_confirm_btn"
                onClick={onReject}
                disabled={!rejectReason.trim()}
                className="bg-red-600 text-white px-4 py-2 rounded-lg disabled:opacity-50 hover:bg-red-700"
              >
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && <LoadingWidget />}
    </>
  );
};

export default StockActionRequestsScreen;
